import { useCallback, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";

export default function BeforeAfter({ before, after, beforeLabel, afterLabel, alt }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, pct)));
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    if (e.currentTarget.setPointerCapture && e.pointerId != null) {
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    }
    setFromClientX(e.clientX);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    setFromClientX(e.clientX);
  };
  const onUp = () => { dragging.current = false; };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      className="relative aspect-square w-full cursor-ew-resize select-none touch-none overflow-hidden rounded-3xl border border-brand-border"
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      {/* After (base) */}
      <img src={after} alt={`${alt} — ${afterLabel}`} className="absolute inset-0 h-full w-full object-cover" loading="lazy" draggable={false} />
      <span className="absolute right-3 top-3 rounded-full bg-brand-primary/90 px-3 py-1 text-xs font-semibold text-white">
        {afterLabel}
      </span>

      {/* Before (clipped) */}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={before}
          alt={`${alt} — ${beforeLabel}`}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ width: ref.current ? `${ref.current.offsetWidth}px` : "100%", maxWidth: "none" }}
          loading="lazy"
          draggable={false}
        />
        <span className="absolute left-3 top-3 rounded-full bg-brand-ink/80 px-3 py-1 text-xs font-semibold text-white">
          {beforeLabel}
        </span>
      </div>

      {/* Handle */}
      <div className="pointer-events-none absolute inset-y-0" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="h-full w-0.5 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.1)]" />
        <div className="absolute top-1/2 left-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-brand-primary shadow-lg">
          <MoveHorizontal className="h-5 w-5" strokeWidth={2} />
        </div>
      </div>
    </div>
  );
}
